/**
 * Background tabs freeze requestAnimationFrame (Chrome/Edge/Firefox).
 * Emscripten WASM main loop depends on rAF, so CAD load stalls when the tab
 * is hidden. Map rAF → setTimeout while hidden so work keeps ticking (throttled).
 *
 * Note: unfocused *windows* often stay visibilityState=visible, so rAF already
 * continues there — this polyfill targets same-window background tabs.
 */
import {
  BACKGROUND_RAF_HIDDEN_FRAME_MS,
  BACKGROUND_RAF_POLYFILL_INSTALL_FLAG,
  DOCUMENT_VISIBILITY_STATE_HIDDEN,
} from '@dental-ui/shared/consts';

export function installBackgroundRafPolyfill(): void {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return;
  }

  const w = window as Window & { [BACKGROUND_RAF_POLYFILL_INSTALL_FLAG]?: boolean };
  if (w[BACKGROUND_RAF_POLYFILL_INSTALL_FLAG]) {
    return;
  }
  w[BACKGROUND_RAF_POLYFILL_INSTALL_FLAG] = true;

  const nativeRAF = window.requestAnimationFrame.bind(window);
  const nativeCAF = window.cancelAnimationFrame.bind(window);

  window.requestAnimationFrame = (callback: FrameRequestCallback): number => {
    if (document.visibilityState === DOCUMENT_VISIBILITY_STATE_HIDDEN) {
      return window.setTimeout(
        () => callback(performance.now()),
        BACKGROUND_RAF_HIDDEN_FRAME_MS
      ) as unknown as number;
    }
    return nativeRAF(callback);
  };

  window.cancelAnimationFrame = (handle: number): void => {
    window.clearTimeout(handle);
    nativeCAF(handle);
  };
}
